import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadOptions } from '../../../utils/selectOptions';
import { ownCheckUrl, bankUrl } from '../../../constants/urls';
import { HOME, PROVEEDORES } from '../../../constants/breadcrumbs';
import Toast from '../../../components/Toast';
import BackButton from '../../../components/BackButton';
import FormActions from '../../../components/FormActions';
import { TUTORIAL_EVENTS } from '../../../constants/tutorials';
import { emitTutorialEvent } from '../../../utils/tutorial';
import CustomInput from '../../../components/Input';
import BasicDatePicker from '../../../components/DatePicker';
import AmountInput from '../../../components/AmountInput';
import AccountBalanceIcon from '@mui/icons-material/AccountBalanceOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';

// ── Estilos reutilizables ─────────────────────────────────────────────────────
const inputCls = (hasError) =>
  `w-full bg-surface-low px-3 py-2.5 rounded-lg border text-sm text-on-surface placeholder:text-gray-400 focus:outline-none focus:ring-2 transition-all ${
    hasError
      ? 'border-red-400 ring-2 ring-red-100'
      : 'border-border-subtle focus:border-blue-lahuerta/40 focus:ring-blue-lahuerta/10'
  }`;

const lockedSelectCls = 'w-full bg-field-locked px-3 py-2.5 rounded-lg border border-field-locked-border text-sm text-on-surface-muted cursor-not-allowed';

const SectionCard = ({ icon, title, children, cols = 3 }) => (
  <section className="space-y-3">
    <div className="flex items-center gap-2 px-1">
      <span className="text-blue-lahuerta">{icon}</span>
      <h2 className="text-base font-semibold text-on-surface">{title}</h2>
    </div>
    <div className={`bg-surface-card p-6 rounded-xl shadow-sm border border-border-subtle grid grid-cols-1 md:grid-cols-${cols} gap-6`}>
      {children}
    </div>
  </section>
);

const FieldError = ({ error, touched }) =>
  touched && error ? <p className="mt-1 text-xs text-red-500">{error}</p> : null;

const FieldLabel = ({ text, help, required = false }) => (
  <div className="flex items-center gap-1.5 mb-1.5">
    <label className="block text-[0.6875rem] font-bold text-on-surface-muted uppercase tracking-wider">{text}{required && ' *'}</label>
    <Tooltip title={help} placement="top" arrow>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: '#9ca3af', cursor: 'help', '&:hover': { color: '#4a7bc4' } }} />
    </Tooltip>
  </div>
);

// ── Componente principal ──────────────────────────────────────────────────────
const OwnCheckForm = () => {
  const [banks, setBanks] = useState([]);
  const [initialValues, setInitialValues] = useState({
    numero: '',
    bank: '',
    amount: '',
    issueDate: null,
    depositDate: null,
    dueDate: null,
    observaciones: '',
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ open: false, message: '' });
  const [usedInPurchase, setUsedInPurchase] = useState(false);
  const [purchaseSupplier, setPurchaseSupplier] = useState('');

  useEffect(() => {
    const load = async () => {
      const loadedBanks = await loadOptions(bankUrl, (data) =>
        data.map((b) => ({ name: b.descripcion, value: b.id }))
      );
      setBanks(loadedBanks);

      if (id) {
        try {
          const response = await axios.get(`${ownCheckUrl}${id}/`);
          const data = response.data;
          setUsedInPurchase((data.purchases?.length || 0) > 0);
          setPurchaseSupplier(data.supplier_name || '');
          setInitialValues({
            numero: data.numero,
            bank: data.banco.id,
            amount: data.importe,
            issueDate: data.fecha_emision,
            depositDate: data.fecha_deposito || null,
            dueDate: data.fecha_vencimiento,
            observaciones: data.observaciones || '',
          });
        } catch (error) {
          console.error('Error al cargar el cheque:', error);
        }
      }
    };
    load();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const validationSchema = Yup.object({
    numero: Yup.number()
      .required('Requerido')
      .positive('Debe ser mayor a cero')
      .integer('Debe ser un número entero'),
    bank: Yup.string().required('Requerido'),
    amount: Yup.number()
      .required('Requerido')
      .positive('El importe debe ser mayor a cero'),
    issueDate: Yup.date().nullable().required('Requerido'),
    depositDate: Yup.date().nullable().test(
      'deposit-before-due',
      'No puede ser posterior a la fecha de vencimiento.',
      function (value) {
        const { dueDate } = this.parent;
        if (!value || !dueDate) return true;
        return new Date(value) <= new Date(dueDate);
      }
    ),
    dueDate: Yup.date().nullable().required('Requerido'),
  });

  const handleSubmit = async (values) => {
    try {
      const payload = {
        numero: values.numero,
        importe: values.amount,
        fecha_emision: values.issueDate,
        fecha_deposito: values.depositDate || null,
        fecha_vencimiento: values.dueDate,
        banco: values.bank,
        observaciones: values.observaciones || null,
      };
      if (id) {
        await axios.put(`${ownCheckUrl}${id}/`, payload);
      } else {
        const response = await axios.post(ownCheckUrl, payload);
        emitTutorialEvent(TUTORIAL_EVENTS.ownCheckCreated, { id: response.data.id });
      }
      navigate('/own-check');
    } catch (error) {
      console.error('Error enviando el formulario:', error);
      const data = error?.response?.data;
      const msg =
        data?.numero?.[0] ||
        data?.fecha_deposito?.[0] ||
        data?.detail ||
        'Error al guardar el cheque.';
      setToast({ open: true, message: msg });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => (
        <Form className="w-full max-w-5xl mx-auto space-y-8 pb-12">
          <Toast
            open={toast.open}
            message={toast.message}
            onClose={() => setToast({ open: false, message: '' })}
          />

          {/* Breadcrumbs + Volver arriba a la derecha, separados del contenido con la misma línea que el pie */}
          <div className="flex items-center justify-between gap-4 pb-4 border-b border-border-subtle">
            <nav className="flex items-center flex-wrap gap-2 text-sm font-medium text-on-surface-muted">
              <span className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors" onClick={() => navigate(HOME.path)}>{HOME.label}</span>
              <span className="text-xs">›</span>
              <span className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors" onClick={() => navigate(PROVEEDORES.path)}>{PROVEEDORES.label}</span>
              <span className="text-xs">›</span>
              <span className="whitespace-nowrap hover:text-accent cursor-pointer transition-colors" onClick={() => navigate('/own-check')}>Cheques emitidos</span>
              <span className="text-xs">›</span>
              <span className="text-on-surface font-semibold">{id ? 'Editar cheque' : 'Nuevo cheque'}</span>
            </nav>
            <BackButton onClick={() => navigate('/own-check')} className="shrink-0 px-5 py-2.5 text-sm" />
          </div>

          {/* 1. Datos del cheque */}
          <SectionCard icon={<AccountBalanceIcon sx={{ fontSize: 20 }} />} title="Datos del Cheque" cols={3}>
            {usedInPurchase && (
              <div className="md:col-span-3 flex items-start gap-3 px-4 py-3 rounded-lg border bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30 text-amber-700 dark:text-amber-400">
                <WarningAmberIcon sx={{ fontSize: 18 }} className="shrink-0 mt-0.5" />
                <p className="text-sm">
                  Este cheque ya está usado en un pago a {purchaseSupplier || 'un proveedor'}. No se puede modificar el número, el banco ni el importe.
                </p>
              </div>
            )}
            <div className="flex flex-col gap-1" data-tour="own-check-number">
              <FieldLabel text="Número" help="Número correlativo del cheque, impreso en la parte superior derecha." required />
              <CustomInput
                name="numero"
                type="number"
                min="1"
                value={values.numero}
                onChange={handleChange}
                placeholder="Nº de cheque"
                disabled={usedInPurchase}
              />
              <FieldError error={errors.numero} touched={touched.numero} />
            </div>
            <div className="flex flex-col gap-1" data-tour="own-check-bank">
              <FieldLabel text="Banco" help="Banco emisor de la cuenta contra la que se libra el cheque, indicado en el membrete superior." required />
              <select
                value={values.bank}
                onChange={(e) => setFieldValue('bank', e.target.value)}
                disabled={usedInPurchase}
                className={usedInPurchase ? lockedSelectCls : inputCls(touched.bank && errors.bank)}
              >
                <option value="">Seleccionar...</option>
                {banks.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.name}</option>
                ))}
              </select>
              <FieldError error={errors.bank} touched={touched.bank} />
            </div>
            <div className="flex flex-col gap-1" data-tour="own-check-amount">
              <FieldLabel text="Importe" help="Monto del cheque, tal como figura escrito en números y en letras en el cuerpo del cheque." required />
              <AmountInput
                name="amount"
                value={values.amount}
                onChange={(raw) => setFieldValue('amount', raw)}
                hasError={touched.amount && Boolean(errors.amount)}
                disabled={usedInPurchase}
              />
              <FieldError error={errors.amount} touched={touched.amount} />
            </div>
          </SectionCard>

          {/* 2. Fechas */}
          <SectionCard icon={<CalendarTodayIcon sx={{ fontSize: 20 }} />} title="Fechas" cols={3}>
            <div className="flex flex-col gap-1" data-tour="own-check-issue-date">
              <FieldLabel text="Fecha de emisión" help="Fecha en la que se libra el cheque, escrita en el campo 'Fecha' del cheque." required />
              <BasicDatePicker
                value={values.issueDate}
                onChange={(date) => setFieldValue('issueDate', date)}
                hasError={touched.issueDate && Boolean(errors.issueDate)}
              />
              <FieldError error={errors.issueDate} touched={touched.issueDate} />
            </div>
            <div className="flex flex-col gap-1" data-tour="own-check-deposit-date">
              <FieldLabel text="Fecha de depósito" help="Fecha desde la cual el cheque puede depositarse (cheque diferido). Si el cheque es a la vista, dejar vacío." />
              <BasicDatePicker
                value={values.depositDate}
                onChange={(date) => setFieldValue('depositDate', date)}
                hasError={touched.depositDate && Boolean(errors.depositDate)}
                clearable
              />
              <FieldError error={errors.depositDate} touched={touched.depositDate} />
            </div>
            <div className="flex flex-col gap-1" data-tour="own-check-due-date">
              <FieldLabel text="Fecha de vencimiento" help="Fecha límite para cobrar el cheque, generalmente 30 días corridos después de la fecha de pago según la ley de cheques." required />
              <BasicDatePicker
                value={values.dueDate}
                onChange={(date) => setFieldValue('dueDate', date)}
                hasError={touched.dueDate && Boolean(errors.dueDate)}
              />
              <FieldError error={errors.dueDate} touched={touched.dueDate} />
            </div>
            <div className="flex flex-col gap-1">
              <FieldLabel text="Observaciones" help="Nota interna opcional para este cheque. No forma parte del cheque físico." />
              <input
                name="observaciones"
                type="text"
                value={values.observaciones}
                onChange={handleChange}
                placeholder="Opcional"
                className={inputCls(false)}
              />
            </div>
          </SectionCard>

          <FormActions
            onCancel={() => navigate('/own-check')}
            confirmType="submit"
            confirmLabel={isSubmitting ? 'Guardando...' : id ? 'Guardar cambios' : 'Registrar Cheque'}
            confirmDisabled={isSubmitting || !validationSchema.isValidSync(values)}
          />

        </Form>
      )}
    </Formik>
  );
};

export default OwnCheckForm;
